// src/components/SubscriptionManager.jsx - NEW FILE

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import './SubscriptionManager.css';

const SubscriptionManager = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { currentUser, userProfile } = useAuth();

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  const fetchSubscriptionDetails = async () => {
    if (!userProfile?.subscriptionId) return;

    try {
      setLoading(true);
      const response = await fetch(`${config.BACKEND_URL}/subscription-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId: userProfile.subscriptionId })
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionDetails(data);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${config.BACKEND_URL}/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: userProfile.subscriptionId,
          userId: currentUser.uid
        })
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      const data = await response.json();
      alert(`Subscription cancelled. You'll retain access until ${new Date(data.endsAt).toLocaleDateString()}`);
      setShowConfirmation(false);
      fetchSubscriptionDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${config.BACKEND_URL}/reactivate-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: userProfile.subscriptionId,
          userId: currentUser.uid
        })
      });

      if (!response.ok) {
        throw new Error('Failed to reactivate subscription');
      }

      alert('Subscription reactivated successfully!');
      fetchSubscriptionDetails();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !subscriptionDetails) {
    return (
      <div className="subscription-overlay" onClick={onClose}>
        <div className="subscription-modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>×</button>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-overlay" onClick={onClose}>
      <div className="subscription-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2>Manage Subscription</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        {subscriptionDetails && (
          <div className="subscription-info">
            <div className="info-row">
              <span className="label">Status:</span>
              <span className={`status ${subscriptionDetails.status}`}>
                {subscriptionDetails.status.toUpperCase()}
              </span>
            </div>
            
            {subscriptionDetails.cancelAtPeriodEnd ? (
              <div className="warning-box">
                <p style={{color: 'black'}}>⚠️ Your subscription is scheduled to cancel on:</p>
                <p className="end-date">
                  {new Date(subscriptionDetails.currentPeriodEnd).toLocaleDateString()}
                </p>
                <p className="small-text">
                  You'll have access to 3 free games after cancellation.
                </p>
                <button 
                  className="reactivate-button"
                  onClick={handleReactivateSubscription}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Reactivate Subscription'}
                </button>
              </div>
            ) : (
              <div className="info-row">
                <span className="label">Renews on:</span>
                <span className="value">
                  {new Date(subscriptionDetails.currentPeriodEnd).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        )}

        {!subscriptionDetails?.cancelAtPeriodEnd && (
          <>
            {!showConfirmation ? (
              <button 
                className="cancel-button"
                onClick={() => setShowConfirmation(true)}
              >
                Cancel Subscription
              </button>
            ) : (
              <div className="confirmation-box">
                <p style={{color: 'black'}}>Are you sure you want to cancel?</p>
                <br/>
                <p className="small-text">
                  You'll keep access until the end of your billing period,
                  then you'll have access to 3 free games.
                </p>
                <div className="button-group">
                  <button 
                    className="confirm-cancel-button"
                    onClick={handleCancelSubscription}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Yes, Cancel'}
                  </button>
                  <button 
                    className="keep-button"
                    onClick={() => setShowConfirmation(false)}
                  >
                    Keep Subscription
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManager;